import {
  CloudPlanItems,
  CmsPlanItems,
  Plan,
  PlanItem,
  PlanType,
  ProductName,
  QuotaLimit,
  ServiceName,
} from "../interfaces"
import { t } from "../lib/i18n"
import logger from "../lib/logger"
import { ServiceNameMap } from "../lib/service"

import { toGigabytes, toNumberOrNull } from "./numberHelpers"
import { quotaLimitLabel } from "./teamQuotaHelper"

type FindProductProps = {
  data: any
  productName: ProductName
}

const findProduct = (props: FindProductProps) => {
  return props.data.find((d: any) => d.attributes.name === props.productName)
}

const formatQuota = (quota: unknown, productName: ProductName): QuotaLimit => {
  // 明示的な null だけが「無制限」。Math.floor(null / n) は 0 になるので先に弾く。
  if (quota === null) return { kind: "unlimited" }

  // キー欠落（undefined）・"" ・"abc" は「無制限」ではなく「読めなかった」。
  // 無制限に倒すとエンフォースしない側に倒れるので fail-closed のまま
  // 上限 0 にする。
  //
  // MEMO: #413 では「プロダクト / 価格が無い」を unmeasured に変えたが、
  // ここは変えていない。値そのものが壊れているケースは #412 が意図して
  // fail-closed にしたもので、別の判断（別 Issue）になる。
  const num = toNumberOrNull(quota)
  if (num === null) {
    // 黙って上限 0 に倒さない。上限 0 はユーザーからは「プランが壊れている」
    // ようにしか見えず、原因の切り分けができなくなる。included_quota は
    // 上限の数値なので個人情報は含まれない。
    logger(
      "formatQuota: included_quota が数値として読めないので上限 0 に倒しました",
      { productName, type: typeof quota, value: quota },
      new Error("unreadable included_quota"),
    )

    return { kind: "limited", value: 0 }
  }

  // MEMO: bandwidth は 1GiB 未満が 0（=「0GB」）になる。案C の CLOUD 帯域は
  // 5GB〜なので今は当たらないが、上限 0 として描かれる点は把握しておくこと。
  return { kind: "limited", value: toGigabytes(num, productName) }
}

type FindProductItemProps = {
  data: any
  planType: PlanType
  productName: ProductName
}

// カタログに「無い」ものを「上限 0」と読まない。TeamQuota 側は行が無いとき
// unmeasuredQuota() を返す（#409 / teamQuotaHelper.ts）のに、こちらは
// limited(0) に倒れていたので、同じレスポンスの同じ欠落が片方だけ上限 0 に
// なっていた（A-4d の 3 区別が片側だけ）。
//
// 黙って倒さない。上限 0 は画面上「プランが壊れている」ようにしか見えず、
// 未計測は「集計中」に見えるだけなので、どちらもログが無いと切り分けられない。
// 出すのはプロダクト名とプラン名だけで、個人情報は含まない。
const unmeasuredPlanItem = (
  props: FindProductItemProps,
  reason: string,
): PlanItem => {
  logger(
    `findProductItem: ${reason}ので未計測として扱いました`,
    { productName: props.productName, planType: props.planType },
    new Error("missing plan quota"),
  )

  return {
    includedQuota: { kind: "unmeasured" },
    price: 0,
  }
}

const findProductItem = (props: FindProductItemProps): PlanItem => {
  const product = findProduct(props)
  if (!product) {
    return unmeasuredPlanItem(props, "カタログにプロダクトが無い")
  }

  const price = product.attributes.prices.data.find(
    (price: any) => price.attributes.plan_name === props.planType,
  )
  if (!price) {
    return unmeasuredPlanItem(props, "そのプランの価格が無い")
  }

  return {
    includedQuota: formatQuota(
      price.attributes.included_quota,
      props.productName,
    ),
    price: price.attributes.price,
  }
}

const cmsPlanItemsDeserializer = (
  data: any,
  planType: PlanType,
): CmsPlanItems => {
  return {
    membership: findProductItem({
      data: data,
      planType: planType,
      productName: "membership",
    }),
    form: findProductItem({
      data: data,
      planType: planType,
      productName: "form",
    }),
    bandwidth: findProductItem({
      data: data,
      planType: planType,
      productName: "bandwidth",
    }),
  }
}

const cloudPlanItemsDeserializer = (
  data: any,
  planType: PlanType,
): CloudPlanItems => {
  return {
    membership: findProductItem({
      data: data,
      planType: planType,
      productName: "membership",
    }),
    site: findProductItem({
      data: data,
      planType: planType,
      productName: "site",
    }),
    customDomain: findProductItem({
      data: data,
      planType: planType,
      productName: "custom_domain",
    }),
    bandwidth: findProductItem({
      data: data,
      planType: planType,
      productName: "bandwidth",
    }),
    buildTime: findProductItem({
      data: data,
      planType: planType,
      productName: "build_time",
    }),
  }
}

// プラン上限の表示文字列。「未計測」を 0 と描かず「上限 0」を無制限と描かない
// 判定は TeamQuota 側と 1 つにしておきたいので、quotaLimitLabel に委譲する
// （ここに同じ switch を 2 本目として書かない）。
export const planQuotaLabel = (includedQuota: QuotaLimit): string =>
  quotaLimitLabel(includedQuota)

// 単位（GB / min）を付けてよいか。「無制限GB」「集計中GB」にしないための唯一の口。
export const hasPlanQuotaUnit = (includedQuota: QuotaLimit): boolean =>
  includedQuota.kind === "limited"

// プランの表示名。CMS と CLOUD で別の名前を持つ。CMS の plan_basic は
// 「CMSベーシック」/ plan_professional は「マーケティング」なので、CLOUD の
// 一覧にそのまま出すと他サービスの名前が並ぶ（ガードを外した実画面で確認した）。
export const planTypeLabel = (
  planType: PlanType,
  serviceName: ServiceName,
): string =>
  serviceName === ServiceNameMap.CLOUD
    ? t(`plan_cloud_${planType}`)
    : t(`plan_${planType}`)

export const planDeserializer = (
  data: any,
  planType: PlanType,
): Plan | undefined => {
  const product = findProduct({
    data: data,
    productName: "subscription",
  })
  if (!product) return

  // そのサービスに存在しないプランを、幽霊プランとして描かない。
  // findProductItem は価格が無いとき { includedQuota: unmeasured, price: 0 } を
  // 返す（#413 で「上限 0」から変えた）。このガードは今も要る: 外すと
  // starter を持たない CMS の一覧に「starter / メンバー数 集計中 / ¥0」が出る。
  // ガードを外して実測した値は price: 0・全項目 unmeasured。
  const hasPrice = product.attributes.prices?.data?.some(
    (price: any) => price.attributes.plan_name === planType,
  )
  if (!hasPrice) return

  const item = findProductItem({
    data: data,
    planType: planType,
    productName: "subscription",
  })

  return {
    id: product.id,
    planType: planType,
    price: item.price,
    serviceName: product.attributes.service_name,
    items:
      product.attributes.service_name === ServiceNameMap.CMS
        ? cmsPlanItemsDeserializer(data, planType)
        : cloudPlanItemsDeserializer(data, planType),
  }
}
